import React, { useState } from "react";
import TrashIcon from "@src/assets/img/trash-icon.svg";
import DownloadIcon from "@src/assets/img/download-icon.svg";
import { useSettings } from "@src/context/SettingsContext";
import { ITrack } from "@src/types/track";
import AlsoFoundBadge from "../AlsoFoundBadge";
import TableActionButton, {
  TableActionButtonContainer,
} from "../TableComponent/TableActionButton";
import TableComponent from "../TableComponent";
import ArtWorkImage from "../ArtWorkImage";
import LargeTextTableCell from "../LargeTextTableCell";
import { useAppDispatch, useAppSelector } from "@src/redux/hooks";
import { RootState } from "@src/redux/store";
import { deleteTrackFromLibrary } from "@src/redux/thunks/libraryThunks";
import UIModal from "../UIModal/UIModal";
import { UIButton } from "../UIButton";
import useModal from "@src/hooks/useModal";
import Spinner from "../Spinner";
import { usePublicS3Downloader } from "@src/hooks/useS3Library";
import PlayButton from "../MusicPlayer/PlayButton";
import CellWithTooltip from "../CellWithTooltip";
import { useRole } from "@src/hooks/useAuth";
import TrackTagsModal from "../TrackTagsModal/TrackTagsModal";
import { useNotification } from "@src/context/NotificationContext";

type LibraryListProps = {
  data: ITrack[];
  tableEndRef: React.RefObject<HTMLTableRowElement>;
  handlePlayButtonClick: (track: ITrack) => void;
  loadingNextPage?: boolean;
};
const LibraryList: React.FC<LibraryListProps> = ({
  tableEndRef,
  data,
  handlePlayButtonClick,
  loadingNextPage,
}): React.JSX.Element => {
  const roleType = useRole();
  const dispatch = useAppDispatch();
  const {
    isDownloading,
    downloadTrackById,
    error: errorDownloading,
  } = usePublicS3Downloader();
  const { addNotification } = useNotification();
  React.useEffect(() => {
    if (errorDownloading) {
      addNotification(errorDownloading, "error");
    }
  }, [errorDownloading]);
  const { tableSettings } = useSettings();
  const [trackToDelete, setTrackToDelete] = useState<ITrack | null>(null);
  const { loading: deleteTrackLoading } = useAppSelector(
    (state) => state.libraryPage.deleteTrackFromLibrary
  );
  const { isOpen, closeModal, openModal } = useModal();
  const { filters } = useAppSelector((state: RootState) => state.libraryPage);
  const {
    isOpen: isTagsModalOpen,
    closeModal: closeTagsModal,
    openModal: openTagsModal,
  } = useModal();
  const [selectedTrackForTags, setSelectedTrackForTags] =
    useState<ITrack | null>(null);
  const { currentPlayingTrack: currentTrack, isPlaying } = useAppSelector(
    (state) => state.trackPlayer
  );
  const columns = [
    {
      Header: "#",
      accessor: "display_row_index",
      width: tableSettings.libraryTable ? "30px" : "40px",
    },
    {
      Header: "",
      accessor: "fileAlbumArt",
      width: "80px",
      hidden: tableSettings.libraryTable,
      Cell: ({ value }: { value: string }): React.JSX.Element => (
        <ArtWorkImage
          $compressedUI={tableSettings.libraryTable}
          value={value}
        />
      ),
    },
    {
      Header: "Title",
      accessor: "title",
      Cell: ({ value }: { value: string }): React.JSX.Element => (
        <CellWithTooltip value={value || "-"} />
      ),
    },
    {
      Header: "Artist",
      accessor: "artist",
      Cell: CellWithTooltip,
    },
    {
      Header: "Album",
      accessor: "album",
      Cell: CellWithTooltip,
    },
    {
      Header: "Label",
      accessor: "label",
      Cell: CellWithTooltip,
    },
    {
      hidden: roleType !== "admin" && roleType !== "manager",
      Header: "ISRC",
      accessor: "isrc",
    },
    {
      Header: "BPM",
      accessor: "bpm",
      Cell: CellWithTooltip,
    },
    {
      Header: "Key",
      accessor: "key",
      Cell: CellWithTooltip,
    },
    {
      Header: "Bitrate",
      accessor: "bitrate",
      Cell: CellWithTooltip,
    },
    {
      Header: "Duration",
      accessor: "duration",
      Cell: CellWithTooltip,
    },
    {
      Header: "Playlists",
      accessor: "foundInPlaylist",
      Cell: ({
        value,
      }: {
        value: ITrack["foundInPlaylist"];
      }): React.JSX.Element => (
        <CellWithTooltip value={value?.map((p) => p.title).join(", ") || "-"} />
      ),
    },
    {
      Header: "Notes",
      accessor: "notes",
      Cell: ({ value }: { value: string }): React.JSX.Element => (
        <>
          <LargeTextTableCell
            $compressedUI={tableSettings.libraryTable}
            value={value}
          />
        </>
      ),
    },
    {
      Header: "Also Found",
      accessor: "alsoFoundColor",
      hidden: roleType === "admin" || roleType === "manager",
      centerContent: true,
      Cell: ({
        value,
        row,
      }: {
        value: string;
        row: ITrack;
      }): React.ReactNode => <AlsoFoundBadge row={row} value={value} />,
    },
    {
      Header: "Likes",
      accessor: "likes",
      hidden: !filters.isTopLikedThisWeek,
      Cell: ({ value }: { value: number }): React.JSX.Element => (
        <CellWithTooltip value={value?.toString() || "0"} />
      ),
    },
    {
      Header: "Player",
      accessor: "audio.url",
      Cell: ({ row }: { row: ITrack }): React.JSX.Element => (
        <PlayButton
          isPlaying={currentTrack?.documentId === row.documentId && isPlaying}
          onClick={() => handlePlayButtonClick(row)}
        />
      ),
      width: "70px",
      minWidth: "60px",
      maxWidth: "90px",
    },
    {
      Header: "Actions",
      accessor: "actions",
      Cell: ({ row }: { row: ITrack }): React.JSX.Element => (
        <TableActionButtonContainer>
          {/* The row itself opens the track's tags, so the action buttons
              keep their clicks to themselves. */}
          <TableActionButton
            onClick={(e) => {
              e.stopPropagation();
              openModal();
              setTrackToDelete(row);
            }}
            alt="delete"
            iconSrc={TrashIcon}
          />
          <TableActionButton
            disabled={isDownloading}
            alt="download"
            iconSrc={DownloadIcon}
            onClick={(e) => {
              e.stopPropagation();
              if (row?.audio?.url) {
                downloadTrackById(row.documentId);
              } else {
                alert("No audio URL found");
              }
            }}
          />
        </TableActionButtonContainer>
      ),
    },
  ];

  const onConfirm = (): void => {
    if (trackToDelete) {
      dispatch(deleteTrackFromLibrary({ trackId: trackToDelete.documentId }));
      setTrackToDelete(null);
      closeModal();
    }
  };

  const onRowClick = (row: ITrack): void => {
    setSelectedTrackForTags(row);
    openTagsModal();
  };
  return (
    <>
      <UIModal
        actions={[
          <UIButton key="cancel" onClick={closeModal} className="secondary">
            Cancel
          </UIButton>,
          <UIButton key="delete" onClick={onConfirm} variant="dangerOutline">
            Delete
          </UIButton>,
        ]}
        isOpen={isOpen}
        title="Delete Track"
        onClose={closeModal}
      >
        <p>
          Are you sure you want to delete the track{" "}
          <strong>{trackToDelete?.title ?? ""}</strong>?
        </p>
        {deleteTrackLoading && <Spinner small />}
      </UIModal>
      <TrackTagsModal
        isOpen={isTagsModalOpen}
        onClose={() => {
          closeTagsModal();
          setSelectedTrackForTags(null);
        }}
        track={selectedTrackForTags}
        onTrackUpdate={(updatedTrack) => {
          setSelectedTrackForTags(updatedTrack);
        }}
        source="library"
      />
      <TableComponent
        loaderRef={tableEndRef}
        $compactUI={tableSettings.libraryTable}
        columns={columns}
        data={data}
        onRowClick={onRowClick}
        loadingNextPage={loadingNextPage}
      />
    </>
  );
};

export default LibraryList;
